import React, { useState, useRef, useEffect } from 'react';
import { Bot, X, Send, Loader2, Trash2, Minimize2, Maximize2, Sparkles } from 'lucide-react';
import { useMatch } from 'react-router-dom';
import api from '../api';
import ReactMarkdown from 'react-markdown';

function Message({ msg }) {
  if (msg.role === 'user') {
    return (
      <div className="flex justify-end" data-testid="chat-user-message">
        <div className="max-w-[80%] px-4 py-2.5 bg-primary text-primary-foreground rounded-2xl rounded-tr-sm text-sm">
          {msg.content}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2" data-testid="chat-ai-message">
      <div className="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center shrink-0 mt-0.5">
        <Bot className="h-4 w-4 text-primary" />
      </div>
      <div className="max-w-[85%] px-4 py-2.5 bg-card border border-border rounded-2xl rounded-tl-sm text-sm prose prose-sm prose-invert max-w-none">
        <ReactMarkdown>{msg.content}</ReactMarkdown>
      </div>
    </div>
  );
}

export function AIChat({ roadmapId: propRoadmapId, roadmapName: propRoadmapName }) {
  // Auto-detect roadmap context from current route
  const roadmapMatch = useMatch('/roadmap/:id');
  const roadmapId = propRoadmapId || roadmapMatch?.params?.id || null;
  const roadmapName = propRoadmapName || null;
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && !minimized) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      inputRef.current?.focus();
    }
  }, [messages, open, minimized]);

  // Greeting on open
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: `Hi! I'm your Upmap AI Copilot${roadmapName ? ` for **${roadmapName}**` : ''}. Ask me anything about your roadmap — risks, priorities, strategic alignment, or what to ship next.`,
      }]);
    }
  }, [open]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const data = await api.request('/api/ai/chat', {
        method: 'POST',
        body: JSON.stringify({
          message: text,
          roadmap_id: roadmapId || null,
          session_id: sessionId,
        }),
      });
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  const clearChat = async () => {
    try {
      await api.request(`/api/ai/chat/${sessionId}`, { method: 'DELETE' });
    } catch {}
    setMessages([{
      role: 'assistant',
      content: 'Chat cleared. How can I help you?',
    }]);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const STARTERS = [
    'What are the biggest risks?',
    'What should we prioritize?',
    'Which items are overdue?',
    'Summarize this roadmap',
  ];

  return (
    <>
      {/* FAB */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-20 right-5 z-50 w-12 h-12 rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
          data-testid="ai-chat-fab"
          aria-label="Open AI Copilot"
        >
          <Sparkles className="h-5 w-5" />
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div
          className={`fixed bottom-20 right-5 z-50 w-[360px] ${minimized ? 'h-14' : 'h-[520px]'} bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden transition-all duration-200`}
          data-testid="ai-chat-window"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-gradient-to-r from-primary/5 to-primary/10 shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center">
                <Bot className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-sm font-semibold">Upmap AI Copilot</p>
                {!minimized && <p className="text-xs text-muted-foreground">Powered by Claude</p>}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {!minimized && (
                <button onClick={clearChat} className="p-1.5 hover:bg-secondary/50 rounded-lg transition-colors" title="Clear chat" data-testid="ai-chat-clear">
                  <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              )}
              <button
                onClick={() => setMinimized(!minimized)}
                className="p-1.5 hover:bg-secondary/50 rounded-lg transition-colors"
                data-testid="ai-chat-minimize"
              >
                {minimized ? <Maximize2 className="h-3.5 w-3.5 text-muted-foreground" /> : <Minimize2 className="h-3.5 w-3.5 text-muted-foreground" />}
              </button>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 hover:bg-secondary/50 rounded-lg transition-colors"
                data-testid="ai-chat-close"
              >
                <X className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </div>
          </div>

          {!minimized && (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
                {messages.map((msg, i) => (
                  <Message key={i} msg={msg} />
                ))}
                {loading && (
                  <div className="flex items-center gap-2" data-testid="chat-loading">
                    <div className="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                      <Bot className="h-4 w-4 text-primary" />
                    </div>
                    <div className="px-4 py-2.5 bg-card border border-border rounded-2xl rounded-tl-sm">
                      <Loader2 className="h-4 w-4 text-primary animate-spin" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Quick starters — show only at start */}
              {messages.length === 1 && (
                <div className="px-4 pb-2 flex flex-wrap gap-1.5" data-testid="chat-starters">
                  {STARTERS.map((s) => (
                    <button
                      key={s}
                      onClick={() => { setInput(s); setTimeout(() => inputRef.current?.focus(), 0); }}
                      className="text-xs px-3 py-1.5 bg-secondary hover:bg-secondary/80 rounded-full border border-border transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}

              {/* Input */}
              <div className="px-3 pb-3 shrink-0">
                <div className="flex items-end gap-2 bg-muted/40 border border-border rounded-xl px-3 py-2">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask about your roadmap..."
                    rows={1}
                    className="flex-1 bg-transparent text-sm resize-none outline-none max-h-24 min-h-[20px]"
                    data-testid="chat-input"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!input.trim() || loading}
                    className="w-8 h-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center hover:opacity-90 disabled:opacity-40 transition-opacity shrink-0"
                    data-testid="chat-send"
                  >
                    <Send className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
